import { useCallback, useEffect, useRef, useState } from 'react';
import { AntAPI } from 'ante-react-kit';

const PAGE_SIZE = 200;

function normalize(str) {
    if (!str) return '';
    return String(str)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .trim();
}

function rowMatchesQuery(row, normQuery) {
    if (!normQuery) return true;
    const tokens = normQuery.split(/\s+/).filter(Boolean);
    const haystack = normalize([
        row.serve_place_name,
        row.serve_place_type,
        row.serve_desc,
        row.serve_note,
        row.fleet_code
    ].filter(Boolean).join(' '));
    return tokens.every((t) => haystack.includes(t));
}

function rowMatchesFilters(row, activeFilters) {
    for (const k of Object.keys(activeFilters)) {
        const set = activeFilters[k];
        if (!set || set.size === 0) continue;
        const v = row[k];
        if (Array.isArray(v)) {
            let hit = false;
            for (const fv of set) { if (v.includes(fv)) { hit = true; break; } }
            if (!hit) return false;
        } else {
            if (!set.has(v)) return false;
        }
    }
    return true;
}

export default function useServePlaceSearch({ enabled, storeCode }) {
    const [query, setQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState({});
    const [allRows, setAllRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const apiRef = useRef(null);
    const reqSeqRef = useRef(0);
    if (!apiRef.current) apiRef.current = new AntAPI('serve', 'ARServeStatus');

    const fetchAll = useCallback(async () => {
        if (!enabled) return;
        const seq = ++reqSeqRef.current;
        setLoading(true);
        try {
            const params = {
                ordering: [
                    { name: 'serve_place_type', type: 'asc' },
                    { name: 'serve_place_name', type: 'asc' }
                ],
                pageIndex: 0,
                pageSize: PAGE_SIZE
            };
            if (storeCode) params.store_code = storeCode;
            const res = await apiRef.current.getList(params);
            if (seq !== reqSeqRef.current) return;
            const rows = (res && res.rows) || [];
            setAllRows(rows);
        } finally {
            if (seq === reqSeqRef.current) setLoading(false);
        }
    }, [enabled, storeCode]);

    useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

    const normQuery = normalize(query);
    const filteredRows = allRows.filter(
        (r) => rowMatchesQuery(r, normQuery) && rowMatchesFilters(r, activeFilters)
    );

    const filterSources = (() => {
        const types = new Map();
        const tagSet = new Set();
        allRows.forEach((r) => {
            if (r.serve_place_type) types.set(r.serve_place_type, (types.get(r.serve_place_type) || 0) + 1);
            if (Array.isArray(r.serve_place_tags)) r.serve_place_tags.forEach((t) => t && tagSet.add(t));
        });
        return {
            types: Array.from(types.entries()).map(([value, count]) => ({ value, count })),
            tags: Array.from(tagSet)
        };
    })();

    const toggleFilter = useCallback((groupKey, val) => {
        setActiveFilters((prev) => {
            const next = { ...prev };
            const cur = new Set(next[groupKey] || []);
            if (cur.has(val)) cur.delete(val); else cur.add(val);
            next[groupKey] = cur;
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setQuery('');
        setActiveFilters({});
    }, []);

    return {
        query, setQuery,
        rows: filteredRows,
        totalCount: filteredRows.length,
        allCount: allRows.length,
        loading, refresh: fetchAll,
        activeFilters, toggleFilter, reset,
        filterSources
    };
}
